import { auth } from '../firebase/config';

/**
 * Video Upload Service
 * Handles uploading videos to Azure Blob Storage via the video-upload-service API
 * @version 1.1.0
 */
const MAX_FILE_SIZE_BYTES = 8 * 1024 * 1024 * 1024; // 8 GB
const MAX_FILE_SIZE_LABEL = '8GB';

class VideoUploadService {
  constructor() {
    // Check if we're in development by looking at the URL
    const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

    // Check for URL parameter override (e.g., ?useRemoteAPIs=true)
    const urlParams = new URLSearchParams(window.location.search);
    const useRemoteAPIs = urlParams.get('useRemoteAPIs') === 'true';

    const envSuffix = import.meta.env.PROD ? 'prod' : 'dev';
    this.apiUrl = (isDevelopment && !useRemoteAPIs)
      ? 'http://localhost:8005'  // Local video upload service
      : `https://video-upload-service-api-${envSuffix}.blackbay-6bd764e8.eastus2.azurecontainerapps.io`;
  }

  /**
   * Get current user's email
   */
  getUserEmail() {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }
    return user.email;
  }

  /**
   * Upload a video file to Azure Blob Storage
   * @param {File} videoFile - The video file to upload
   * @param {Function} onProgress - Optional progress callback (0-100)
   * @param {number} duration - Optional video duration in seconds (for validation)
   * @param {string} mode - Optional video mode: 'singles', 'workload_drills', or 'stroke_drills'
   * @returns {Promise<Object>} Upload result with blob_path and other details
   */
  async uploadVideo(videoFile, onProgress = null, duration = null, mode = 'singles') {
    try {
      if (videoFile.size > MAX_FILE_SIZE_BYTES) {
        const sizeMB = (videoFile.size / (1024 * 1024)).toFixed(0);
        throw new Error(`File too large (${sizeMB}MB). Maximum size is ${MAX_FILE_SIZE_LABEL}.`);
      }

      const email = this.getUserEmail();

      const formData = new FormData();
      formData.append('video', videoFile);
      formData.append('email', email);
      if (duration !== null) {
        formData.append('duration', duration.toString());
      }
      // Only add mode field if it's workload_drills or stroke_drills (not singles)
      if (mode === 'workload_drills' || mode === 'stroke_drills') {
        formData.append('mode', mode);
      }

      // Get Firebase ID token for authentication
      const user = auth.currentUser;
      const idToken = user ? await user.getIdToken() : null;

      // Use XMLHttpRequest for progress tracking
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        // Track upload progress
        if (onProgress) {
          xhr.upload.addEventListener('progress', (event) => {
            if (event.lengthComputable) {
              const percentComplete = Math.round((event.loaded / event.total) * 100);
              onProgress(percentComplete);
            }
          });
        }

        xhr.addEventListener('load', () => {
          try {
            const response = JSON.parse(xhr.responseText);
            if (xhr.status >= 200 && xhr.status < 300) {
              if (response.success) {
                resolve(response);
              } else {
                reject(new Error(response.error || 'Upload failed'));
              }
            } else {
              reject(new Error(response.error || `Upload failed with status ${xhr.status}`));
            }
          } catch (e) {
            reject(new Error('Failed to parse server response'));
          }
        });

        xhr.addEventListener('error', () => {
          reject(new Error('Network error during upload'));
        });

        xhr.addEventListener('abort', () => {
          reject(new Error('Upload cancelled'));
        });

        xhr.open('POST', `${this.apiUrl}/api/upload`);
        if (idToken) {
          xhr.setRequestHeader('X-Firebase-Token', idToken);
        }
        xhr.send(formData);
      });

    } catch (error) {
      console.error('Video upload failed:', error);
      throw error;
    }
  }

  /**
   * Upload a video from a URL (fetches the video first, then uploads)
   * Note: This only works for URLs that allow CORS
   * @param {string} videoUrl - URL of the video to upload
   * @param {string} filename - Filename to use for the upload
   * @param {Function} onProgress - Optional progress callback (0-100)
   * @param {number} duration - Optional video duration in seconds (for validation)
   * @param {string} mode - Optional video mode: 'singles', 'workload_drills', or 'stroke_drills'
   * @returns {Promise<Object>} Upload result
   */
  async uploadVideoFromUrl(videoUrl, filename, onProgress = null, duration = null, mode = 'singles') {
    try {
      // Fetch the video
      if (onProgress) onProgress(0);
      
      const response = await fetch(videoUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch video: ${response.status}`);
      }

      const blob = await response.blob();
      const videoFile = new File([blob], filename, { type: blob.type || 'video/mp4' });

      // Upload the file
      return await this.uploadVideo(videoFile, onProgress, duration, mode);

    } catch (error) {
      console.error('Video upload from URL failed:', error);
      throw error;
    }
  }

  /**
   * Check if a video already exists in storage
   * @param {string} filename - Name of the video file
   * @returns {Promise<Object>} Status object with exists boolean
   */
  async checkUploadStatus(filename) {
    try {
      const email = this.getUserEmail();
      const user = auth.currentUser;
      const idToken = user ? await user.getIdToken() : null;

      const headers = {};
      if (idToken) {
        headers['X-Firebase-Token'] = idToken;
      }

      const response = await fetch(
        `${this.apiUrl}/api/upload-status?filename=${encodeURIComponent(filename)}&email=${encodeURIComponent(email)}`,
        {
          method: 'GET',
          headers,
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Check failed: ${response.status}`);
      }

      return await response.json();

    } catch (error) {
      console.error('Upload status check failed:', error);
      throw error;
    }
  }

  /**
   * Check service health
   * @returns {Promise<Object>} Health status
   */
  async checkHealth() {
    try {
      const response = await fetch(`${this.apiUrl}/health`);
      if (!response.ok) {
        throw new Error(`Health check failed: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Health check failed:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const videoUploadService = new VideoUploadService();

export default videoUploadService;
